import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { settingsApi } from "../api";

const schema = z.object({
  portal_name: z.string().min(1).max(128),
  tagline: z.string().min(1).max(256),
  show_tile_count_badges: z.boolean(),
  default_card_size: z.enum(["small", "medium", "large"]),
});
type FormValues = z.infer<typeof schema>;

export default function SettingsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: settingsApi.get });
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (data) {
      reset({
        portal_name: data.portal_name,
        tagline: data.tagline,
        show_tile_count_badges: !!data.show_tile_count_badges,
        default_card_size: data.default_card_size,
      });
    }
  }, [data, reset]);

  const save = useMutation({
    mutationFn: (v: FormValues) => settingsApi.update(v),
    onSuccess: () => {
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["content"] });
    },
    onError: (err) => toast.error((err as { message?: string }).message ?? "Save failed"),
  });

  return (
    <div className="max-w-2xl mx-auto px-10 py-10">
      <header className="mb-7">
        <h1 className="display text-[36px] font-medium tracking-tight">
          <span className="italic">Settings</span>
        </h1>
        <p className="text-[13.5px] text-ink-500 mt-1.5">Portal-wide presentation options.</p>
      </header>

      <form onSubmit={handleSubmit((v) => save.mutate(v))} className="surface p-6 space-y-4">
        <div>
          <label className="label">Portal name</label>
          <input className="input" {...register("portal_name")} />
        </div>
        <div>
          <label className="label">Tagline</label>
          <input className="input" {...register("tagline")} />
        </div>
        <div>
          <label className="label">Default card size</label>
          <select className="input" {...register("default_card_size")}>
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-[13px] cursor-pointer">
          <input type="checkbox" {...register("show_tile_count_badges")} />
          Show service count badges on department cards
        </label>
        <div className="pt-3 flex justify-end gap-2 border-t border-ink-100 mt-5">
          <button type="submit" disabled={isSubmitting || !isDirty} className="btn-primary">
            {isSubmitting ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
